import React, { useMemo, useState } from "react";
import { Sun, CaretDown, CaretUp, Fire } from "@phosphor-icons/react";

function fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

const CONFIDENCE_COLOR = {
  high: "#FF3B1F",
  nominal: "#FF8A1F",
  low: "#FFC74A",
};

export default function ViirsHotspotsCard({ hotspots = [], onFlyTo, frpHeatmap = false, onToggleHeatmap }) {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => {
    const high = hotspots.filter((h) => h.confidence === "high").length;
    const nominal = hotspots.filter((h) => h.confidence === "nominal").length;
    const totalFrp = hotspots.reduce((s, h) => s + (h.frp || 0), 0);
    const maxFrp = hotspots.reduce((m, h) => Math.max(m, h.frp || 0), 0);
    const latest = hotspots
      .map((h) => h.acq_datetime)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    return { high, nominal, total: hotspots.length, latest, totalFrp, maxFrp };
  }, [hotspots]);

  if (summary.total === 0) return null;

  // Sort by recency, then by confidence
  const sorted = [...hotspots].sort((a, b) => {
    const t = (b.acq_datetime || "").localeCompare(a.acq_datetime || "");
    if (t !== 0) return t;
    const rank = (c) => (c === "high" ? 2 : c === "nominal" ? 1 : 0);
    return rank(b.confidence) - rank(a.confidence);
  });

  return (
    <section
      data-testid="viirs-hotspots-card"
      className="border-b border-tac-border bg-tac-surface/40"
    >
      <button
        type="button"
        data-testid="viirs-card-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sun size={14} weight="fill" className="text-orange-400" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
            WA · Satellite Hotspots · 24h
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-faint">
            · {summary.total}
          </span>
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <>
          <div className="px-4 pb-2 flex items-center gap-3 text-[10px] font-mono-tac tabular flex-wrap">
            <span className="text-tac-dim">
              <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: CONFIDENCE_COLOR.high }} />
              {summary.high} high
            </span>
            <span className="text-tac-dim">
              <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: CONFIDENCE_COLOR.nominal }} />
              {summary.nominal} nominal
            </span>
            <span className="text-tac-dim">peak {summary.maxFrp.toFixed(0)} MW</span>
            {summary.latest && (
              <span className="text-tac-faint ml-auto">latest {fmtTime(summary.latest)}</span>
            )}
          </div>

          {onToggleHeatmap && (
            <div className="px-4 pb-2">
              <button
                type="button"
                data-testid="frp-heatmap-toggle"
                onClick={onToggleHeatmap}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  frpHeatmap
                    ? "bg-tac-primary text-white border-tac-primary"
                    : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text"
                }`}
                title="Scale marker size by Fire Radiative Power (megawatts)"
              >
                <Fire size={11} weight="fill" />
                FRP heatmap {frpHeatmap ? "on" : "off"}
              </button>
            </div>
          )}

          <ul className="divide-y divide-tac-border/50" data-testid="viirs-list">
            {sorted.slice(0, 6).map((h) => (
              <li key={h.id} data-testid={`viirs-row-${h.id}`} className="px-4 py-2">
                <button
                  type="button"
                  onClick={() => onFlyTo?.(h.latitude, h.longitude)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: CONFIDENCE_COLOR[h.confidence] || CONFIDENCE_COLOR.nominal }}
                      />
                      <span className="font-mono-tac tabular text-[11px] text-tac-text truncate group-hover:text-tac-primary transition-colors">
                        {h.latitude.toFixed(3)}, {h.longitude.toFixed(3)}
                      </span>
                    </div>
                    <span className="text-[15px] font-mono-tac tabular text-tac-faint shrink-0">
                      {fmtTime(h.acq_datetime)}
                    </span>
                  </div>
                  {h.frp != null && (
                    <div className="text-[15px] font-mono-tac tabular text-tac-dim pl-4 mt-0.5">
                      FRP {h.frp.toFixed(1)} MW · {h.daynight === "D" ? "Day" : h.daynight === "N" ? "Night" : ""}
                    </div>
                  )}
                </button>
              </li>
            ))}
            {sorted.length > 6 && (
              <li className="px-4 py-1.5 text-[15px] font-mono-tac text-tac-faint text-center">
                + {sorted.length - 6} more on map
              </li>
            )}
          </ul>
        </>
      )}
      <div className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        Source: NASA FIRMS · VIIRS NOAA-20 (375m) + MODIS Terra/Aqua (1km)
      </div>
    </section>
  );
}
